import React, { useState, useEffect } from 'react';
import { Book, X, RotateCcw, BookOpen, ChevronRight } from 'lucide-react';

/**
 * FloatingGuideButton - Persistent floating button for Go Live Guide access
 * 
 * Features:
 * - Always visible in bottom-left corner (on reviewer session page)
 * - Pulses gently to attract attention without being annoying
 * - Click to open menu with options:
 *   - Resume tour (if in progress)
 *   - Start from beginning
 *   - Open full guide page
 * - Remembers if user completed tour
 */

const STORAGE_KEY = 'mcm_go_live_tour_progress';
const COMPLETED_KEY = 'mcm_go_live_tour_completed';

const FloatingGuideButton = ({ onOpenTour, onStartFresh, onOpenGuidePage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasProgress, setHasProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const progress = localStorage.getItem(STORAGE_KEY);
    const completed = localStorage.getItem(COMPLETED_KEY);
    
    if (progress) {
      try {
        const parsed = JSON.parse(progress);
        setHasProgress(true);
        setCurrentStep(parsed.step || 0);
      } catch (e) {
        console.error('Failed to parse tour progress:', e);
      }
    }
    
    setHasCompleted(completed === 'true');
  }, []);

  // Listen for storage changes (in case tour updates)
  useEffect(() => {
    const handleStorage = () => {
      const progress = localStorage.getItem(STORAGE_KEY);
      const completed = localStorage.getItem(COMPLETED_KEY);
      
      if (progress) {
        try {
          const parsed = JSON.parse(progress);
          setHasProgress(true);
          setCurrentStep(parsed.step || 0);
        } catch (e) {
          setHasProgress(false);
        }
      } else {
        setHasProgress(false);
      }
      
      setHasCompleted(completed === 'true');
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleResumeTour = () => {
    setIsMenuOpen(false);
    onOpenTour?.();
  };

  const handleStartFresh = () => {
    // Clear progress from localStorage
    localStorage.removeItem(STORAGE_KEY);
    setHasProgress(false);
    setCurrentStep(0);
    setIsMenuOpen(false);
    
    // Use the dedicated onStartFresh if available (forces remount)
    // Otherwise fall back to regular onOpenTour
    if (onStartFresh) {
      onStartFresh();
    } else {
      setTimeout(() => {
        onOpenTour?.();
      }, 50);
    }
  };

  const handleOpenGuide = () => {
    setIsMenuOpen(false);
    onOpenGuidePage?.();
  };

  return (
    <>
      {/* Main floating button */}
      <div 
        className="fixed bottom-20 right-4 z-[9999] flex flex-col items-center gap-1" 
        data-testid="floating-guide-button"
        style={{
          animation: !hasCompleted && !isMenuOpen ? 'gentle-sway 3s ease-in-out infinite' : 'none'
        }}
      >
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className={`
            relative w-14 h-14 rounded-2xl shadow-lg 
            flex items-center justify-center
            transition-all duration-300 hover:scale-110
            ${isMenuOpen 
              ? 'bg-[#F2D857] text-black' 
              : 'bg-gradient-to-br from-[#F2D857] to-[#C9A227] text-black'
            }
          `}
          style={{
            boxShadow: '0 4px 20px rgba(242, 216, 87, 0.4)',
          }}
        >
          {isMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Book className="w-7 h-7" style={{ strokeWidth: 2.5 }} />
          )}
          
          {/* Progress indicator dot - shows if tour in progress but not completed */}
          {hasProgress && !hasCompleted && !isMenuOpen && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
              !
            </span>
          )}
        </button>
        
        {/* Guide label */}
        {!isMenuOpen && (
          <span className="text-[10px] font-bold text-[#F2D857] uppercase tracking-wider">
            Guide
          </span>
        )}

        {/* Menu popup */}
        {isMenuOpen && (
          <div 
            className="absolute bottom-16 right-0 w-64 bg-[#111] rounded-xl border border-[#333] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
            data-testid="guide-menu"
          >
            {/* Header */}
            <div className="p-3 bg-gradient-to-r from-[#F2D857]/20 to-transparent border-b border-[#333]">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Book className="w-4 h-4 text-[#F2D857]" />
                Go Live Guide
              </h3>
              {hasProgress && (
                <p className="text-xs text-gray-400 mt-1">
                  Step {currentStep + 1} of 6 in progress
                </p>
              )}
            </div>

            {/* Options */}
            <div className="p-2">
              {hasProgress && (
                <button
                  onClick={handleResumeTour}
                  className="w-full p-3 rounded-lg hover:bg-[#1a1a1a] transition flex items-center gap-3 text-left group"
                >
                  <div className="w-8 h-8 rounded-full bg-[#F2D857]/20 flex items-center justify-center">
                    <ChevronRight className="w-4 h-4 text-[#F2D857]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">Resume Tour</p>
                    <p className="text-gray-500 text-xs">Continue where you left off</p>
                  </div>
                </button>
              )}

              <button
                onClick={handleStartFresh}
                className="w-full p-3 rounded-lg hover:bg-[#1a1a1a] transition flex items-center gap-3 text-left group"
              >
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <RotateCcw className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">
                    {hasProgress ? 'Start Over' : 'Start Tour'}
                  </p>
                  <p className="text-gray-500 text-xs">
                    {hasProgress ? 'Begin from the beginning' : 'Learn how to go live'}
                  </p>
                </div>
              </button>

              <button
                onClick={handleOpenGuide}
                className="w-full p-3 rounded-lg hover:bg-[#1a1a1a] transition flex items-center gap-3 text-left group"
              >
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Full Guide</p>
                  <p className="text-gray-500 text-xs">Open reference page</p>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 4px 20px rgba(242, 216, 87, 0.4);
          }
          50% {
            box-shadow: 0 4px 30px rgba(242, 216, 87, 0.7), 0 0 40px rgba(242, 216, 87, 0.3);
          }
        }
        
        @keyframes gentle-sway {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-6px);
          }
        }
      `}</style>
    </>
  );
};

export default FloatingGuideButton;
